import { useMemo, useState } from 'react';
import {
  Stack,
  Title,
  Card,
  Group,
  Text,
  SimpleGrid,
  Select,
  Badge,
  Table,
  Progress,
  Box,
  Tooltip,
  Alert,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconChartBar,
  IconChartLine,
  IconClipboardList,
  IconClockHour4,
  IconPercentage,
  IconTrendingUp,
  IconTrendingDown,
  IconAlertCircle,
  IconReportAnalytics,
  IconTruck,
  IconUser,
  IconFileCertificate,
} from '@tabler/icons-react';
import { DonutChart, LineChart } from '@mantine/charts';
import { useDeCAs, useVehiculos, useConductores } from '../../api/hooks';
import type { DeCaEnriched } from '../../api/types';
import { CatalogPageHeader } from '../../components/CatalogPageHeader';

/**
 * Página de Informes — métricas globales sobre el tenant.
 *
 * Vista agregada (todo en el cliente a partir de useDeCAs() — sin nueva
 * API). Mostra:
 *  1. Selector de período (mes / año / todo) — filtra las graficas y KPIs.
 *  2. 4 KPIs: emitidos en período, % modificados, tiempo medio de
 *     generacion, comparativa interanual.
 *  3. Grafica temporal: DeCAs agrupados por mes (últimos 12).
 *  4. Top vehículos / Top conductores (ranking por numero de DeCAs).
 *  5. Distribución por estado (donut).
 *  6. Tabla resumen mensual.
 *
 * Consideraciones legales / metricas:
 *  - "Tiempo medio de generacion" usa `fechaEmision - createdAt` como proxy
 *    (en MVP no hay un campo `fechaSolicitud` separado; el alta y la
 *    emision son atomicas en `POST /deca`).
 *  - "% modificados" cuenta DeCAs con `estado === 'MODIFICATED'` O con
 *    mas de 1 version en `versiones[]`. La segunda fuente es mas fiable
 *    porque el estado puede quedarse en VIGENTE aunque se haya
 *    modificado.
 */
export function Informes() {
  const { data: decas = [], isLoading, error } = useDeCAs();
  const { data: vehiculos = [] } = useVehiculos();
  const { data: conductores = [] } = useConductores();

  // Selector de ventana temporal. Default: "este año".
  const [ventana, setVentana] = useState<'mes' | 'anio' | 'todo'>('anio');

  // ──────── Métricas globales ────────
  const hoy = useMemo(() => new Date(), []);
  const inicioMes = useMemo(() => new Date(hoy.getFullYear(), hoy.getMonth(), 1), [hoy]);
  const inicioAnio = useMemo(() => new Date(hoy.getFullYear(), 0, 1), [hoy]);
  const inicioAnioAnterior = useMemo(() => new Date(hoy.getFullYear() - 1, 0, 1), [hoy]);
  const finAnioAnterior = useMemo(() => new Date(hoy.getFullYear(), 0, 1), [hoy]);

  const enPeriodo = (d: DeCaEnriched, inicio: Date, fin?: Date): boolean => {
    const t = new Date(d.fechaEmision).getTime();
    return t >= inicio.getTime() && (!fin || t < fin.getTime());
  };

  const decasEnVentana = useMemo(() => {
    if (ventana === 'mes') return decas.filter((d) => enPeriodo(d, inicioMes));
    if (ventana === 'anio') return decas.filter((d) => enPeriodo(d, inicioAnio));
    return decas;
  }, [decas, ventana, inicioMes, inicioAnio]);

  const decasAnioAnterior = useMemo(
    () => decas.filter((d) => enPeriodo(d, inicioAnioAnterior, finAnioAnterior)),
    [decas, inicioAnioAnterior, finAnioAnterior],
  );

  // Total emitidos
  const totalEmitidos = decasEnVentana.length;

  // % modificados: al menos 2 versiones
  const modificados = decasEnVentana.filter((d) => d.versiones.length >= 2).length;
  const pctModificados = totalEmitidos === 0 ? 0 : Math.round((modificados / totalEmitidos) * 100);

  // Tiempo medio generacion en segundos → minutos
  const tiempoMedioSeg = useMemo(() => {
    if (decasEnVentana.length === 0) return 0;
    let total = 0;
    let n = 0;
    for (const d of decasEnVentana) {
      if (!d.createdAt) continue;
      const diff = (new Date(d.fechaEmision).getTime() - new Date(d.createdAt).getTime()) / 1000;
      // Cap superior a 24h para evitar outliers por migraciones / seeds viejos
      if (diff >= 0 && diff <= 24 * 60 * 60) {
        total += diff;
        n++;
      }
    }
    return n === 0 ? 0 : total / n;
  }, [decasEnVentana]);

  // Comparativa interanual
  const comparativaAnual = useMemo(() => {
    const actual = decasEnVentana.length;
    const anterior = decasAnioAnterior.length;
    if (anterior === 0) return null;
    const delta = ((actual - anterior) / anterior) * 100;
    return { actual, anterior, delta };
  }, [decasEnVentana, decasAnioAnterior]);

  // ──────── Grafica temporal: ultimos 12 meses ────────
  const datosMensuales = useMemo(() => {
    const months: { mes: string; isoMonth: string; emitidos: number; modificados: number }[] = [];
    const fmt = new Intl.DateTimeFormat('es-ES', { month: 'short', year: '2-digit' });
    for (let i = 11; i >= 0; i--) {
      const date = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
      const next = new Date(hoy.getFullYear(), hoy.getMonth() - i + 1, 1);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months.push({
        isoMonth: key,
        mes: fmt.format(date),
        emitidos: 0,
        modificados: 0,
      });
      // Rellenamos con decas de ese mes
      for (const d of decas) {
        const t = new Date(d.fechaEmision);
        if (t >= date && t < next) {
          months[months.length - 1].emitidos += 1;
          if (d.versiones.length >= 2) {
            months[months.length - 1].modificados += 1;
          }
        }
      }
    }
    return months;
  }, [decas, hoy]);

  // ──────── Top vehiculos / conductores ────────
  const topVehiculos = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of decasEnVentana) {
      const id = d.vehiculo?.id ?? d.vehiculoId;
      const label = d.vehiculo?.matricula ?? id;
      const k = `${label}::${id}`;
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([k, count]) => {
        const [label, id] = k.split('::');
        return { label, id, count };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [decasEnVentana]);

  const topConductores = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of decasEnVentana) {
      const id = d.conductor?.id ?? d.conductorId;
      const label = d.conductor?.nombre ?? id;
      const k = `${label}::${id}`;
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([k, count]) => {
        const [label, id] = k.split('::');
        return { label, id, count };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [decasEnVentana]);

  // ──────── Distribución por estado ────────
  const distEstados = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const d of decasEnVentana) {
      counts[d.estado] = (counts[d.estado] ?? 0) + 1;
    }
    const colorPorEstado: Record<string, string> = {
      VIGENTE: 'deca.6',
      EN_CURSO: 'deca.5',
      FINALIZADO: 'gray.6',
      CANCELADO: 'red.5',
    };
    return Object.entries(counts).map(([estado, value]) => ({
      name: estado,
      value,
      color: colorPorEstado[estado] ?? 'gray.5',
    }));
  }, [decasEnVentana]);

  // ──────── Resumen mensual (para la tabla) ────────
  const resumenMensual = useMemo(() => {
    const meses: {
      key: string;
      label: string;
      emitidos: number;
      modificados: number;
      pctModif: number;
    }[] = [];
    for (let i = 11; i >= 0; i--) {
      const date = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
      const next = new Date(hoy.getFullYear(), hoy.getMonth() - i + 1, 1);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric' }).format(
        date,
      );
      const emitidas = decas.filter((d) => {
        const t = new Date(d.fechaEmision);
        return t >= date && t < next;
      });
      const mods = emitidas.filter((d) => d.versiones.length >= 2).length;
      meses.push({
        key,
        label,
        emitidos: emitidas.length,
        modificados: mods,
        pctModif: emitidas.length === 0 ? 0 : Math.round((mods / emitidas.length) * 100),
      });
    }
    // Solo meses con datos (para no mostrar 12 filas vacias)
    return meses.filter((m) => m.emitidos > 0).reverse();
  }, [decas, hoy]);

  // ──────── Total vehículos / conductores activos (referencia) ────────
  const totalVehiculosActivos = vehiculos.filter((v) => v.activo).length;
  const totalConductoresActivos = conductores.filter((c) => c.activo).length;

  return (
    <Stack gap="md">
      <CatalogPageHeader
        title="Informes"
        subtitle="Métricas globales de actividad del tenant. Calculadas en el cliente sobre los datos cargados."
        icon={IconReportAnalytics}
        newLabel="Exportar CSV"
        onNew={() => {
          // Exportar el resumen mensual como CSV — mismo shape que la tabla,
          // separado por comas, cabecera utf-8.
          const csv =
            '\uFEFF' +
            'Mes,Emitidos,Modificados,%Modificados\n' +
            resumenMensual
              .map((m) => `${m.label},${m.emitidos},${m.modificados},${m.pctModif}`)
              .join('\n');
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `decaplus-informes-${new Date().toISOString().slice(0, 10)}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        }}
        filters={
          <Select
            value={ventana}
            onChange={(v) => v && setVentana(v as 'mes' | 'anio' | 'todo')}
            data={[
              { value: 'mes', label: 'Este mes' },
              { value: 'anio', label: 'Este año' },
              { value: 'todo', label: 'Todo el histórico' },
            ]}
            w={180}
          />
        }
      />

      {error && (
        <Alert
          icon={<IconAlertCircle size={20} />}
          color="red"
          variant="light"
          title="No se pudieron cargar los DeCAs"
        >
          Verifica tu conexión y vuelve a intentarlo.
        </Alert>
      )}

      {isLoading ? (
        <Center mih={300}>
          <Loader />
        </Center>
      ) : (
        <>
          {/* KPIs */}
          <SimpleGrid cols={{ base: 1, md: 2, lg: 4 }} spacing="md">
            <KpiCard
              icon={IconFileCertificate}
              label="DeCAs emitidos"
              value={totalEmitidos.toLocaleString('es-ES')}
              hint={`En ${ventanaLabel(ventana)}`}
              color="deca"
            />
            <KpiCard
              icon={IconPercentage}
              label="Modificados"
              value={`${pctModificados}%`}
              hint={`${modificados} de ${totalEmitidos} con ≥ 2 versiones`}
              color="yellow"
              progress={pctModificados}
            />
            <KpiCard
              icon={IconClockHour4}
              label="Tiempo medio de generación"
              value={
                tiempoMedioSeg === 0
                  ? '—'
                  : tiempoMedioSeg < 60
                    ? `${Math.round(tiempoMedioSeg)} s`
                    : tiempoMedioSeg < 3600
                      ? `${Math.round(tiempoMedioSeg / 60)} min`
                      : `${(tiempoMedioSeg / 3600).toFixed(1)} h`
              }
              hint="Desde alta hasta emisión (proxy: createdAt → fechaEmision)"
              color="blue"
            />
            <KpiCard
              icon={
                comparativaAnual && comparativaAnual.delta >= 0 ? IconTrendingUp : IconTrendingDown
              }
              label="vs. mismo período año anterior"
              value={
                comparativaAnual
                  ? `${comparativaAnual.delta >= 0 ? '+' : ''}${Math.round(comparativaAnual.delta)}%`
                  : '—'
              }
              hint={
                comparativaAnual
                  ? `${comparativaAnual.actual} vs ${comparativaAnual.anterior} del año anterior`
                  : 'Sin datos del año anterior'
              }
              color={comparativaAnual && comparativaAnual.delta >= 0 ? 'teal' : 'red'}
            />
          </SimpleGrid>

          {/* Gráfica temporal */}
          <Card withBorder padding="md">
            <Group justify="space-between" mb="md">
              <Group gap="xs">
                <IconChartLine size={20} color="var(--mantine-color-deca-7)" />
                <Title order={4}>DeCAs emitidos por mes (últimos 12 meses)</Title>
              </Group>
              <Group gap="xs">
                <Badge variant="light" color="deca">
                  Emitidos
                </Badge>
                <Badge variant="light" color="yellow">
                  Modificados
                </Badge>
              </Group>
            </Group>
            {datosMensuales.some((m) => m.emitidos > 0) ? (
              <LineChart
                h={260}
                data={datosMensuales}
                dataKey="mes"
                series={[
                  { name: 'emitidos', color: 'deca.6', label: 'Emitidos' },
                  { name: 'modificados', color: 'yellow.6', label: 'Modificados' },
                ]}
                curveType="linear"
                withDots
                withLegend={false}
                yAxisProps={{ allowDecimals: false }}
              />
            ) : (
              <EmptyChartState />
            )}
          </Card>

          {/* Top vehículos / Top conductores */}
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <Card withBorder padding="md">
              <Group gap="xs" mb="md">
                <IconTruck size={20} color="var(--mantine-color-deca-7)" />
                <Title order={4}>Top vehículos</Title>
              </Group>
              <Text size="xs" c="dimmed" mb="md">
                {totalVehiculosActivos} vehículos activos en el catálogo
              </Text>
              {topVehiculos.length === 0 ? (
                <EmptyChartState />
              ) : (
                <RankingList
                  items={topVehiculos}
                  max={Math.max(...topVehiculos.map((v) => v.count))}
                />
              )}
            </Card>
            <Card withBorder padding="md">
              <Group gap="xs" mb="md">
                <IconUser size={20} color="var(--mantine-color-deca-7)" />
                <Title order={4}>Top conductores</Title>
              </Group>
              <Text size="xs" c="dimmed" mb="md">
                {totalConductoresActivos} conductores activos en el catálogo
              </Text>
              {topConductores.length === 0 ? (
                <EmptyChartState />
              ) : (
                <RankingList
                  items={topConductores}
                  max={Math.max(...topConductores.map((v) => v.count))}
                />
              )}
            </Card>
          </SimpleGrid>

          {/* Distribución por estado + Resumen mensual */}
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <Card withBorder padding="md">
              <Group gap="xs" mb="md">
                <IconChartBar size={20} color="var(--mantine-color-deca-7)" />
                <Title order={4}>Distribución por estado</Title>
              </Group>
              {distEstados.length === 0 ? (
                <EmptyChartState />
              ) : (
                <Group justify="center" align="center">
                  <DonutChart
                    data={distEstados}
                    size={200}
                    thickness={28}
                    paddingAngle={2}
                    withLabelsLine={false}
                    withTooltip
                    chartLabel={totalEmitidos.toLocaleString('es-ES')}
                    valueFormatter={(v) => v.toLocaleString('es-ES')}
                  />
                  <Stack gap={4}>
                    {distEstados.map((s) => (
                      <Group key={s.name} gap="xs">
                        <Box w={10} h={10} bg={s.color} style={{ borderRadius: 2 }} />
                        <Text size="sm">{s.name}</Text>
                        <Text size="sm" c="dimmed">
                          {s.value}
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                </Group>
              )}
            </Card>

            <Card withBorder padding="md">
              <Group gap="xs" mb="md">
                <IconClipboardList size={20} color="var(--mantine-color-deca-7)" />
                <Title order={4}>Resumen mensual</Title>
              </Group>
              {resumenMensual.length === 0 ? (
                <EmptyChartState />
              ) : (
                <Table withTableBorder withColumnBorders striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Mes</Table.Th>
                      <Table.Th ta="right">Emitidos</Table.Th>
                      <Table.Th ta="right">Modificados</Table.Th>
                      <Table.Th>%</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {resumenMensual.map((m) => (
                      <Table.Tr key={m.key}>
                        <Table.Td>
                          <Text size="sm" tt="capitalize">
                            {m.label}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm" ff="monospace">
                            {m.emitidos}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm" ff="monospace">
                            {m.modificados}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={6} wrap="nowrap">
                            <Progress
                              value={m.pctModif}
                              size="sm"
                              color={m.pctModif > 0 ? 'yellow' : 'gray'}
                              w={70}
                            />
                            <Text size="xs" c="dimmed">
                              {m.pctModif}%
                            </Text>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Card>
          </SimpleGrid>
        </>
      )}
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Subcomponentes
// ─────────────────────────────────────────────────────────────────────

function ventanaLabel(v: 'mes' | 'anio' | 'todo'): string {
  if (v === 'mes') return 'este mes';
  if (v === 'anio') return 'este año';
  return 'todo el histórico';
}

interface KpiCardProps {
  icon: typeof IconChartBar;
  label: string;
  value: string;
  hint?: string;
  color?: string;
  /** 0..100, opcional, pinta una barra fina debajo del valor. */
  progress?: number;
}
function KpiCard({ icon: IconCmp, label, value, hint, color = 'deca', progress }: KpiCardProps) {
  return (
    <Card withBorder padding="md">
      <Stack gap={6}>
        <Group gap={6}>
          <IconCmp size={16} color={`var(--mantine-color-${color}-7)`} />
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '.04em' }}>
            {label}
          </Text>
        </Group>
        <Text size="xl" fw={700} ff="monospace" c={`${color}.${color === 'gray' ? '7' : '7'}`}>
          {value}
        </Text>
        {progress !== undefined && (
          <Progress
            value={progress}
            size="xs"
            color={color === 'deca' ? 'deca' : (color as 'red' | 'teal' | 'yellow' | 'blue')}
          />
        )}
        {hint && (
          <Text size="xs" c="dimmed">
            {hint}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

interface RankingItem {
  label: string;
  id: string;
  count: number;
}

function RankingList({ items, max }: { items: RankingItem[]; max: number }) {
  return (
    <Stack gap={8}>
      {items.map((it, idx) => (
        <Box key={it.id ?? it.label}>
          <Group justify="space-between" mb={4}>
            <Group gap={6}>
              <Badge size="sm" variant="light" color="deca" w={26}>
                {idx + 1}
              </Badge>
              <Text size="sm" fw={500}>
                {it.label}
              </Text>
            </Group>
            <Tooltip label={`${it.count} DeCAs`} withArrow>
              <Text size="sm" ff="monospace" fw={600}>
                {it.count}
              </Text>
            </Tooltip>
          </Group>
          <Progress value={max === 0 ? 0 : (it.count / max) * 100} size="sm" color="deca" />
        </Box>
      ))}
    </Stack>
  );
}

function EmptyChartState() {
  return (
    <Center mih={120}>
      <Stack align="center" gap={4}>
        <Text size="sm" c="dimmed">
          No hay datos en el período seleccionado.
        </Text>
        <Text size="xs" c="dimmed">
          Cambia el filtro de arriba o emite más DeCAs para ver métricas.
        </Text>
      </Stack>
    </Center>
  );
}
